import { Injectable } from '@angular/core';
import * as queries from '@app/core/graphql/queries';
import {
    CreateProviderInput,
    Provider,
    UpdateProviderInput
} from '@app/core/services/api/API.service';

import { generateClient } from '@aws-amplify/api';
import * as mutations from '@graphql/mutations';

/** Consulta ligera para listados/selects; evita timeouts del listProviders completo. */
const LIST_PROVIDERS_FOR_SELECT = /* GraphQL */ `
    query ListProvidersForSelect($limit: Int, $nextToken: String) {
        listProviders(limit: $limit, nextToken: $nextToken) {
            items {
                id
                comertial_name
                isApproved
                isProvider
                isPremium
                isPop
                isPrinted
                bussiness_name {
                    name
                    rfc
                    __typename
                }
                image {
                    key
                    url
                    __typename
                }
                logo {
                    key
                    url
                    __typename
                }
                __typename
            }
            nextToken
            __typename
        }
    }
`;

/** Detalle/edición admin: sin File.palette ni Address.name para no romper getProvider. */
const GET_PROVIDER_FOR_ADMIN = /* GraphQL */ `
    query GetProviderForAdmin($id: ID!) {
        getProvider(id: $id) {
            id
            isApproved
            isProvider
            isPremium
            isPop
            isPrinted
            comertial_name
            bussiness_name {
                rfc
                name
                tax_residence {
                    street
                    outdoor_number
                    inside_number
                    colony
                    delegation
                    state
                    city
                    postal_code
                    chain
                    __typename
                }
                __typename
            }
            contact {
                first_name
                last_name
                email
                mobile_phone
                local_phone
                __typename
            }
            address {
                street
                outdoor_number
                inside_number
                colony
                delegation
                state
                city
                postal_code
                chain
                __typename
            }
            image {
                key
                url
                __typename
            }
            logo {
                key
                url
                __typename
            }
            created
            updated
            createdAt
            updatedAt
            __typename
        }
    }
`;

type ListProvidersPage = {
    items?: Provider[];
    nextToken?: string | null;
};

@Injectable({
    providedIn: 'root'
})
export class ProviderService {
    constructor() {}
    private amplifyClient = generateClient();

    private async fetchProvidersPage(
        query: string,
        limit: number,
        nextToken?: string | null
    ): Promise<ListProvidersPage | null> {
        const result = (await this.amplifyClient.graphql({
            query,
            variables: {
                limit,
                nextToken: nextToken ?? undefined
            }
        })) as { data?: { listProviders?: ListProvidersPage } };

        return result.data?.listProviders ?? null;
    }

    async listProviders(limit = 500): Promise<Provider[]> {
        try {
            const items: Provider[] = [];
            let nextToken: string | null | undefined = undefined;

            do {
                const page = await this.fetchProvidersPage(
                    LIST_PROVIDERS_FOR_SELECT,
                    limit,
                    nextToken
                );
                if (page?.items?.length) {
                    items.push(...(page.items.filter(Boolean) as Provider[]));
                }
                nextToken = page?.nextToken ?? null;
            } while (nextToken);

            if (items.length > 0) {
                return items;
            }

            return this.listProvidersWithFullQuery(limit);
        } catch (error) {
            console.error('Error listing providers (light query):', error);
            return this.listProvidersWithFullQuery(limit);
        }
    }

    private async listProvidersWithFullQuery(limit = 500): Promise<Provider[]> {
        try {
            const items: Provider[] = [];
            let nextToken: string | null | undefined = undefined;

            do {
                const page = await this.fetchProvidersPage(
                    queries.listProviders,
                    limit,
                    nextToken
                );
                if (page?.items?.length) {
                    items.push(...(page.items.filter(Boolean) as Provider[]));
                }
                nextToken = page?.nextToken ?? null;
            } while (nextToken);

            return items;
        } catch (error) {
            console.error('Error listing providers (full query):', error);
            return [];
        }
    }

    async getProvidersByIds(ids: string[]): Promise<Provider[]> {
        const uniqueIds = [...new Set(ids.filter(Boolean))];
        if (!uniqueIds.length) {
            return [];
        }

        const providers = await Promise.all(
            uniqueIds.map(id => this.getProvider(id))
        );

        return providers.filter(Boolean) as Provider[];
    }

    async getProvider(id: string): Promise<Provider> {
        const adminProvider = await this.fetchProviderByQuery(
            GET_PROVIDER_FOR_ADMIN,
            id
        );
        if (adminProvider) {
            return adminProvider;
        }

        return this.fetchProviderByQuery(queries.getProvider, id);
    }

    private async fetchProviderByQuery(
        query: string,
        id: string
    ): Promise<Provider | null> {
        try {
            const result = (await this.amplifyClient.graphql({
                query,
                variables: { id }
            })) as { data?: { getProvider?: Provider | null } };
            const data = result.data?.getProvider;
            if (data) {
                return data as Provider;
            }
        } catch (error) {
            console.error('Error finding Provider', error);
        }
        return null;
    }

    async createProvider(input: CreateProviderInput): Promise<Provider> {
        try {
            const provider = (
                await this.amplifyClient.graphql({
                    query: mutations.createProvider,
                    variables: {
                        input
                    }
                })
            ).data.createProvider;
            if (provider) return provider as Provider;
        } catch (error) {
            console.error('Error creating provider', error);
            return null;
        }
    }
    async updateProvider(input: UpdateProviderInput): Promise<Provider> {
        try {
            const provider = (
                await this.amplifyClient.graphql({
                    query: mutations.updateProvider,
                    variables: {
                        input
                    }
                })
            ).data.updateProvider;
            if (provider) return provider as Provider;
        } catch (error) {
            console.error('Error updating providers', error);
            return null;
        }
    }
}
